// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/prop-types */
import React, { useMemo, useEffect, useRef } from 'react';
import { graphql } from 'gatsby';

import { Navigation, Pagination, A11y } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { GLULayout } from '../../layouts/glu';
import { MainHeader } from '../../components/molecules/main-header';
import { Card } from '../../components/glu/molecules/course-card';

import { CardContainer } from '../../components/glu/organisms/course-container';
import PageSection from '../../components/glu/organisms/page-section';
import TiltedCardContainer from '../../components/glu/organisms/tilted-card';
import CatchUpWebinars from '../../components/glu/organisms/catch-up-webinars';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/bundle';
import { PBSLink } from '../../components/atoms/link';
import { LikeButton } from '../../components/glu/atoms/like-button';
import ArrowIcon from '../../assets/arrow.inline.svg';
import { Head } from '../../components/templates/head';
import {
	getOrganizationSeoSchema,
	getBreadcrumbsSeoSchema,
	getImageSeoSchema,
} from '../../utils/seo-schema';
import { pageTypes } from '../../utils/url-helpers';
import { BannerNav } from '../../components/glu/organisms/banner-nav';

const GluClientPage = ({ data, location }) => {
	const {
		eLearningCoursesContent,
		featuredContent,
		featuredContentSlide,
		eLearningTitle,
		eLearningSessionCards,
		courses: courseLinks,
		locale,
	} = data.datoCmsGluClientPage;
	const { mainLogo } = data.allDatoCmsGluLogo.nodes[0];
	const { nodes: allGluCoursePages } = data.allDatoCmsGluCourseLink;
	const { nodes: id } = data.allCourses;
	const allCourseArray = id.flatMap((e) => e.id);

	const headerCTAContent = {
		...data.datoCmsGluClientPage.hero,
		header: data.datoCmsGluClientPage.hero.header.value.document.children,
	};

	const metaContent = {
		noIndex: data.datoCmsGluClientPage.noIndex,
		noFollow: data.datoCmsGluClientPage.noFollow,
		metaInformation: data.datoCmsGluClientPage.seo,
		fallbackTitle: data.datoCmsGluClientPage.eLearningTitle,
		fallbackDescription: '',
		canonicalUrl: data.datoCmsGluClientPage?.canonicalUrl || location?.href,
	};

	const seoSchemas = {
		organization: getOrganizationSeoSchema({ data, url: location?.origin }),
		breadcrumbs: getBreadcrumbsSeoSchema({
			data,
			pageType: pageTypes.gluClientPage,
			location,
		}),
		image: getImageSeoSchema(data?.hero?.primaryImage?.url),
	};

	const onDemandCourses = data?.allDatoCmsGluCourse;
	const categoryIds = data.allCourseCategories.nodes;

	useMemo(() => {
		const allCourseCategories = [];
		const allCourseLinksCategories = [];

		[...categoryIds, ...allGluCoursePages].forEach((e) => {
			// eslint-disable-next-line compat/compat
			fetch(`/api/likes?item=${e.id}`)
				.then((res) => res.json())
				.then((courseData) => {
					const obj = {
						id: e.id,
						likes: courseData.likes,
					};
					if (obj.likes > 0) {
						if (
							allCourseArray.some((course) =>
								course.includes(e.id)
							)
						) {
							allCourseCategories.push(obj);
						} else {
							allCourseLinksCategories.push(obj);
						}
					}
				})
				// eslint-disable-next-line no-console
				.catch((err) => console.error(err));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDemandRef = useRef();
	const coursesRef = useRef();

	const scroll = (anchor) => {
		const top = anchor === 'demand' ? onDemandRef : coursesRef;
		setTimeout(() => {
			window.scrollTo({
				top: top?.current?.offsetTop,
				left: 0,
				behavior: 'smooth',
			});
		}, 100);
	};

	useEffect(() => {
		const isBrowser = typeof window !== 'undefined';
		if (isBrowser) {
			if (window.location.href.includes('#on-demand')) {
				scroll('demand');
			}
			if (window.location.href.includes('#courses')) {
				scroll('courses');
			}
		}
	});
	const bannerTitle = 'Love it, Share it';
	const { locale: urlLocale } = data.datoCmsGluClientPage;
	return (
		<GoogleReCaptchaProvider
			reCaptchaKey={process.env.GATSBY_RECAPTCHA_KEY}
		>
			<Head metaContent={metaContent} seoSchemas={seoSchemas} />
			{bannerTitle && <BannerNav title={bannerTitle} />}
			<MainHeader
				urlLocale={urlLocale}
				{...headerCTAContent}
				glu
				logo={mainLogo}
			/>
			{featuredContent && featuredContentSlide.length > 0 && (
				<PageSection
					theme="Inherit"
					titleBlock={featuredContent.value}
					className="py-12"
				>
					<Swiper
						modules={[Navigation, Pagination, A11y]}
						spaceBetween={50}
						slidesPerView={1}
						navigation
						pagination={{ clickable: true }}
					>
						{featuredContentSlide.map((course) => (
							<SwiperSlide key={course.id}>
								<div className="m-16 rounded-sm bg-brand-pale-400">
									<div className="flex flex-row p-6 sm:p-12">
										<div className="flex flex-col justify-between w-full lg:w-3/5">
											<div>
												<div className="flex gap-3">
													<LikeButton
														likes={3042}
														courseId={course.id}
													/>
												</div>
												<h3 className="my-6 text-lg font-bold sm:text-2xl">
													{course.title}
												</h3>

												{course.intro && (
													<p className="my-6 text-md sm:text-xl">
														{course.intro}
													</p>
												)}
											</div>

											<div>
												{course?.link && (
													<PBSLink
														to={course.link}
														size="Small"
														className="!text-sm lg:w-max w-full justify-center"
														target="_blank"
													>
														Start course{' '}
														<ArrowIcon
															viewBox="0 0 33 34"
															className="w-4 h-auto ml-3 pt-[1px] -scale-x-100"
														/>
													</PBSLink>
												)}

												{course?.slug && (
													<PBSLink
														to={course.slug}
														size="Small"
														className="!text-sm w-full lg:w-max justify-center"
													>
														Watch{' '}
														<ArrowIcon
															viewBox="0 0 33 34"
															className="w-4 h-auto ml-3 pt-[1px] -scale-x-100"
														/>
													</PBSLink>
												)}
											</div>
										</div>
										<div className="relative items-center hidden w-full lg:w-2/5 lg:flex">
											<div className="m-auto ">
												{course.thumbnail?.url ? (
													<img
														src={
															course.thumbnail.url
														}
														alt={
															course.thumbnail
																.url || ''
														}
														className="border-8 border-white"
													/>
												) : (
													<img
														src={mainLogo.url}
														alt="GLU logo"
													/>
												)}
											</div>
										</div>
									</div>

									<div className="flex items-center px-12 py-3 text-white rounded-b-sm bg-brand-blue-400">
										{course.timeToWatchInMinutes && (
											<>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="19"
													height="19"
													fill="none"
													className="inline-block mr-2"
												>
													<path
														fill="#fff"
														fillRule="evenodd"
														d="M9.5 2.997a.527.527 0 0 0-.527.527v5.912c0 .29.236.527.527.527h5.517a.527.527 0 1 0 0-1.055h-4.99V3.524a.527.527 0 0 0-.527-.527Z"
														clipRule="evenodd"
													/>
													<path
														fill="#fff"
														fillRule="evenodd"
														d="M9.5.436c-4.966 0-9 4.034-9 9 0 4.965 4.034 9 9 9s9-4.035 9-9c0-4.966-4.034-9-9-9Zm0 1.054a7.94 7.94 0 0 1 7.946 7.946A7.94 7.94 0 0 1 9.5 17.38a7.94 7.94 0 0 1-7.946-7.945A7.94 7.94 0 0 1 9.5 1.49Z"
														clipRule="evenodd"
													/>
												</svg>
												Duration{' '}
												{course.timeToWatchInMinutes}{' '}
												minutes
											</>
										)}
									</div>
								</div>
							</SwiperSlide>
						))}
					</Swiper>
				</PageSection>
			)}
			{courseLinks.length > 0 ? (
				<div ref={coursesRef}>
					<PageSection
						theme="Inherit"
						titleBlock={eLearningCoursesContent.value}
						logo={mainLogo}
						className="pb-4"
					>
						<div className="my-4 md:flex md:gap-4">
							{courseLinks.map((course) => (
								<Card
									cardContent={course}
									key={course.id}
									logo={mainLogo}
									locale={locale}
								/>
							))}
						</div>
					</PageSection>
				</div>
			) : null}
			<div ref={onDemandRef}>
				<CardContainer
					cardContainerContent={{
						id: 'course-container',
					}}
					cardVariant="courseList"
					data={onDemandCourses}
					associatedCategory={
						onDemandCourses?.nodes?.[0]?.associatedCategory
					}
					locale={locale}
				/>
			</div>
			<PageSection
				titleBlock={eLearningTitle.value}
				logo={mainLogo}
				className="pb-4"
			>
				<TiltedCardContainer
					cards={eLearningSessionCards}
					className="pb-0"
					locale={locale}
				/>
			</PageSection>
			<PageSection>
				<CatchUpWebinars data={data.catchUpWebinar} locale={locale} />
			</PageSection>
		</GoogleReCaptchaProvider>
	);
};

const WrappedGluClientPage = ({ data, location }) => (
	<GLULayout locale={data.datoCmsGluClientPage.locale}>
		<GluClientPage data={data} location={location} />
	</GLULayout>
);

export default WrappedGluClientPage;

export const query = graphql`
	query GluCLientPage($slug: String!, $title: String, $locale: String!) {
		datoCmsGluClientPage(slug: { eq: $slug }, locale: { eq: $locale }) {
			id: originalId
			noIndex
			noFollow
			locale
			seo {
				title
				description
				image {
					url
				}
				twitterCard
			}
			title
			slug
			hero {
				header {
					value
				}
				secondaryHeader
				primaryLinkCopy
				primaryLink
				primaryLinkOpenForm
				secondaryLinkCopy
				secondaryLink
				primaryImage {
					alt
					url
					gatsbyImageData(
						width: 400
						height: 400
						layout: CONSTRAINED
					)
				}
				secondaryImage {
					alt
					url
					gatsbyImageData(
						width: 380
						height: 330
						layout: CONSTRAINED
					)
				}
				showTrustPilot
			}
			featuredContent {
				value
			}
			featuredContentSlide {
				... on DatoCmsGluCourse {
					id
					locale
					title
					slug
					intro
					category {
						title
						slug
					}
					thumbnail {
						alt
						url
					}
				}
				... on DatoCmsGluCourseLink {
					id
					locale
					title
					intro
					category {
						title
						slug
					}
					thumbnail {
						alt
						url
					}
				}
			}
			eLearningCoursesContent {
				value
			}
			courses {
				id: originalId
				link
				title
				intro
				showCpdLogo
				associatedCategory
				thumbnail {
					alt
					gatsbyImageData(
						aspectRatio: 53
						placeholder: DOMINANT_COLOR
						width: 412
						height: 162
					)
				}
			}
			eLearningTitle {
				value
			}
			eLearningSessionCards {
				title
				image {
					gatsbyImageData
					alt
				}
				description {
					value
				}
				ctaLink
				ctaText
			}
		}
		filteredCourses: allDatoCmsGluCourse(
			limit: 3
			filter: {
				category: { title: { eq: $title } }
				locale: { eq: $locale }
			}
		) {
			nodes {
				id: originalId
				title
				intro
				slug
				thumbnail {
					alt
					gatsbyImageData(
						aspectRatio: 53
						placeholder: DOMINANT_COLOR
						width: 350
						height: 350
					)
				}
				category {
					title
					slug
				}
				timeToWatchInMinutes
			}
		}
		allDatoCmsGluCourse(
			sort: { order: ASC, fields: title }
			limit: 1
			filter: {
				associatedCategory: { eq: $title }
				isWebinarCatchUp: { ne: true }
				hideInOnDemandLibrary: { ne: true }
				locale: { eq: $locale }
			}
		) {
			totalCount
			nodes {
				associatedCategory
			}
		}
		allDatoCmsGluLogo(filter: { locale: { eq: "en" } }) {
			nodes {
				mainLogo {
					url
					alt
				}
			}
		}
		allDatoCmsGluCourseLink(
			filter: {
				associatedCategory: { eq: $title }
				locale: { eq: $locale }
			}
		) {
			nodes {
				id: originalId
				link
				title
				intro
				associatedCategory
				showCpdLogo
				thumbnail {
					alt
					gatsbyImageData(
						aspectRatio: 53
						placeholder: DOMINANT_COLOR
						width: 412
						height: 162
					)
				}
			}
		}
		allCourseCategories: allDatoCmsGluCourse(
			sort: { order: ASC, fields: title }
			filter: {
				associatedCategory: { eq: $title }
				isWebinarCatchUp: { ne: true }
				hideInOnDemandLibrary: { ne: true }
				locale: { eq: $locale }
			}
		) {
			nodes {
				id: originalId
			}
		}
		allCourses: allDatoCmsGluCourse(filter: { locale: { eq: $locale } }) {
			nodes {
				id
			}
		}
		catchUpWebinar: datoCmsCatchUpWebinar(locale: { eq: $locale }) {
			catchUpTitle {
				value
			}
			catchUpWebinarsDescription {
				value
			}
			catchUpWebinarsCta
			catchUpWebinarsCtaSlug
		}
	}
`;
